"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, Grid, Plus, MessageSquare, User } from "lucide-react";
import { useEffect, useState } from "react";

interface NavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  ariaLabel: string;
  isFloating?: boolean;
}

const navItems: NavItem[] = [
  {
    href: "/",
    label: "홈",
    icon: Home,
    ariaLabel: "홈으로 이동",
  },
  {
    href: "/list",
    label: "검색",
    icon: Grid,
    ariaLabel: "검색하기",
  },
  {
    href: "/product/new",
    label: "등록",
    icon: Plus,
    ariaLabel: "상품 등록하기",
    isFloating: true,
  },
  {
    href: "/chat",
    label: "채팅",
    icon: MessageSquare,
    ariaLabel: "채팅 목록",
  },
  {
    href: "/mypage?tab=profile",
    label: "마이페이지",
    icon: User,
    ariaLabel: "마이페이지",
  },
];

export function BottomNav() {
  const pathname = usePathname();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!isMobile) {
    return null;
  }

  const hideOnPaths = [
    "/auth/login",
    "/auth/signup",
    "/auth/reset-password",
    "/auth/find-email",
  ];
  if (hideOnPaths.some((path) => pathname?.startsWith(path))) {
    return null;
  }

  const isActive = (href: string) => {
    if (href === "/") {
      return pathname === "/";
    }
    if (href === "/list") {
      return (
        pathname?.startsWith("/list") ||
        pathname?.startsWith("/categories") ||
        pathname?.startsWith("/category")
      );
    }
    if (href === "/product/new") {
      return (
        pathname === "/product/new" ||
        pathname === "/post" ||
        pathname === "/upload"
      );
    }
    if (href === "/mypage") {
      return (
        pathname?.startsWith("/mypage") || pathname?.startsWith("/profile")
      );
    }
    return pathname?.startsWith(href);
  };

  const regularItems = navItems.filter((item) => !item.isFloating);
  const floatingItem = navItems.find((item) => item.isFloating);
  const isFloatingActive = floatingItem ? isActive(floatingItem.href) : false;

  return (
    <>
      {/* Main Navigation Bar */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white z-[100] safe-area-inset-bottom"
        aria-label="하단 네비게이션"
        style={{
          borderTop: "1px solid #EAEAEA",
          boxShadow: "0 -1px 3px rgba(0, 0, 0, 0.08)",
        }}
      >
        <div className="relative flex items-center justify-around h-[68px] px-2 max-w-screen-sm mx-auto">
          {/* Left side: Home and Category */}
          <div className="flex items-center justify-center flex-1 gap-0">
            {regularItems.slice(0, 2).map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className="flex flex-col items-center justify-center flex-1 min-h-[48px] px-1 touch-manipulation active:opacity-70 transition-opacity"
                  aria-label={item.ariaLabel}
                  style={{
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    height: "100%",
                  }}
                >
                  <div
                    className="flex flex-col items-center justify-center"
                    style={{ gap: "4px" }}
                  >
                    <Icon
                      className={`w-[22px] h-[22px] transition-all duration-200 flex-shrink-0 ${
                        active ? "text-[#7A56E8]" : "text-[#C4C4C4]"
                      }`}
                      strokeWidth={active ? 2.5 : 2}
                    />
                    <span
                      className={`text-[10px] font-medium transition-all duration-200 text-center ${
                        active ? "text-[#7A56E8]" : "text-[#C4C4C4]"
                      }`}
                      style={{
                        lineHeight: "1.2",
                      }}
                    >
                      {item.label}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Center: Empty space for floating button */}
          <div className="w-[60px] flex-shrink-0" aria-hidden="true" />

          {/* Right side: Chat and My Page */}
          <div className="flex items-center justify-center flex-1 gap-0">
            {regularItems.slice(2).map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className="flex flex-col items-center justify-center flex-1 min-h-[48px] px-1 touch-manipulation active:opacity-70 transition-opacity"
                  aria-label={item.ariaLabel}
                  style={{
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    height: "100%",
                  }}
                >
                  <div
                    className="flex flex-col items-center justify-center"
                    style={{ gap: "4px" }}
                  >
                    <Icon
                      className={`w-[22px] h-[22px] transition-all duration-200 flex-shrink-0 ${
                        active ? "text-[#7A56E8]" : "text-[#C4C4C4]"
                      }`}
                      strokeWidth={active ? 2.5 : 2}
                    />
                    <span
                      className={`text-[10px] font-medium transition-all duration-200 text-center ${
                        active ? "text-[#7A56E8]" : "text-[#C4C4C4]"
                      }`}
                      style={{
                        lineHeight: "1.2",
                      }}
                    >
                      {item.label}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Floating Action Button (FAB) */}
      {floatingItem && (
        <div className="md:hidden fixed bottom-[24px] left-1/2 -translate-x-1/2 z-[101] max-w-screen-sm mx-auto px-4">
          <Link
            href={floatingItem.href}
            className="relative touch-manipulation block active:scale-[0.96] transition-transform duration-150 ease-out"
            aria-label={floatingItem.ariaLabel}
          >
            <div
              className="w-[60px] h-[60px] rounded-full flex items-center justify-center shadow-[0_4px_16px_rgba(122,86,232,0.18)]"
              style={{
                background: "linear-gradient(135deg, #7A56E8, #9B82FF)",
              }}
            >
              <Plus
                className="w-6 h-6 text-white"
                strokeWidth={3}
              />
            </div>
          </Link>
        </div>
      )}
    </>
  );
}
